import React from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { useGetCustomer } from '../../../services/customerService';
import type { Birthday, Customer } from '../../../services/schemas/customerServiceSchemas';
import { describeBirthday, parseLocalDate } from '../../../utils/birthdays';
import { getLegalEntityType } from '../../../utils/validation/legalEntityType';

const PERSON_LABELS: Record<string, string> = {
  DIRECTOR: 'Director',
  INDIVIDUAL: 'Personal customer',
  REPRESENTATIVE: 'Representative'
};

const CONTACT_ICONS: Record<string, string> = {
  EMAIL: 'fa-envelope',
  MOBILE: 'fa-mobile',
  PHONE: 'fa-phone',
  LANDLINE: 'fa-phone',
  WHATSAPP: 'fa-whatsapp',
  SMS: 'fa-comment',
  FAX: 'fa-fax'
};

interface ContactLike {
  contactType?: string;
  contactValue?: string;
  primary?: boolean;
}

const contactHref = (c: ContactLike) =>
  c.contactType === 'EMAIL' ? `mailto:${c.contactValue}` : `tel:${(c.contactValue || '').replace(/\s+/g, '')}`;

/** Contacts as clickable rows: email opens the mail client, numbers dial on a phone. */
const ContactList: React.FC<{ contacts?: ContactLike[]; empty: string }> = ({ contacts, empty }) => {
  const usable = (contacts || []).filter(c => c.contactValue);
  if (usable.length === 0) {
    return <div className="text-muted fst-italic small">{empty}</div>;
  }
  return (
    <ul className="list-unstyled mb-0">
      {usable.map((c, i) => (
        <li key={`${c.contactType}-${c.contactValue}-${i}`} className="d-flex align-items-center mb-2">
          <i className={`fa ${CONTACT_ICONS[c.contactType || ''] || 'fa-address-book'} text-primary me-2`} style={{ width: 16 }}></i>
          <a href={contactHref(c)} className="text-decoration-none text-break">{c.contactValue}</a>
          <small className="text-muted ms-2">{(c.contactType || '').toLowerCase()}</small>
          {c.primary && <span className="badge bg-primary-subtle text-primary-emphasis ms-2">Primary</span>}
        </li>
      ))}
    </ul>
  );
};

const Detail: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="col-sm-6">
    <div className="small text-muted">{label}</div>
    <div className={value ? 'fw-semibold' : 'fst-italic text-muted'}>{value || 'Not recorded'}</div>
  </div>
);

const formatDate = (value?: string) =>
  value ? parseLocalDate(value).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }) : undefined;

interface Props {
  birthday: Birthday | null;
  onHide: () => void;
}

/**
 * A quick look at the person whose birthday it is and the customer behind them - how to reach
 * them first, then who the customer is - without leaving the Birthdays page.
 */
const BirthdayCustomerModal: React.FC<Props> = ({ birthday, onHide }) => {
  const { data: customer, isLoading, isError } = useGetCustomer(
    birthday?.customerNumber ? { customerNumber: birthday.customerNumber } : undefined,
    { query: { enabled: !!birthday?.customerNumber, staleTime: 60_000 } }
  ) as { data?: Customer; isLoading: boolean; isError: boolean };

  const info = customer?.info as any;
  const isCompany = customer?.type === 'CORPORATE';
  const director = birthday?.personType === 'DIRECTOR'
    ? (info?.directors || []).find((d: any) => d?.id === birthday.personId)
    : undefined;
  const activeDirectors = (info?.directors || []).filter((d: any) => d && (!d.status || d.status === 'ACTIVE'));

  return (
    <Modal show={!!birthday} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa' }}>
        <Modal.Title className="d-flex align-items-center">
          <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f0f7ff', border: '2px solid #e3f2fd' }}>
            <i className="fa fa-birthday-cake text-primary"></i>
          </div>
          <div>
            <div className="fw-bold fs-5">{birthday?.name}</div>
            <small className="text-muted fw-normal">
              {PERSON_LABELS[birthday?.personType || ''] || birthday?.personType}
              {birthday?.companyName ? ` · ${birthday.companyName}` : ''}
            </small>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
        {birthday && (
          <>
            {/* The person */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3"><i className="fa fa-user text-primary me-2"></i>Birthday</h6>
                <div className="row g-3 mb-3">
                  <Detail label="Birthday" value={`${describeBirthday(birthday.birthday)} · ${formatDate(birthday.birthday)}`} />
                  <Detail label="Turning" value={birthday.turningAge} />
                  <Detail
                    label="Date of birth"
                    value={birthday.dateOfBirth
                      ? `${formatDate(birthday.dateOfBirth)}${birthday.dateOfBirthSource === 'ID_NUMBER' ? ' (from ID number)' : ''}`
                      : undefined}
                  />
                  {director && <Detail label="Role" value={director.designation} />}
                </div>

                <div className="row g-3">
                  <div className="col-sm-6">
                    <div className="small text-muted mb-1">Cell phone</div>
                    {birthday.phone ? (
                      <a href={`tel:${birthday.phone.replace(/\s+/g, '')}`} className="fw-semibold text-decoration-none">
                        <i className="fa fa-mobile text-success me-2"></i>{birthday.phone}
                      </a>
                    ) : <div className="fst-italic text-muted">None on record</div>}
                  </div>
                  <div className="col-sm-6">
                    <div className="small text-muted mb-1">Email</div>
                    {birthday.email ? (
                      <a href={`mailto:${birthday.email}`} className="fw-semibold text-decoration-none text-break">
                        <i className="fa fa-envelope text-info me-2"></i>{birthday.email}
                      </a>
                    ) : <div className="fst-italic text-muted">None on record</div>}
                    {birthday.email && birthday.emailSource === 'COMPANY' && (
                      <div><small className="text-muted">Company email</small></div>
                    )}
                  </div>
                </div>

                {director && (
                  <div className="mt-3 pt-3 border-top">
                    <div className="small text-muted mb-2">All of {birthday.name}'s own contacts</div>
                    <ContactList contacts={director.contacts} empty="No contacts of their own; the company's are below." />
                  </div>
                )}
              </div>
            </div>

            {/* The customer */}
            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3">
                  <i className={`fa ${isCompany ? 'fa-building' : 'fa-id-card'} text-primary me-2`}></i>
                  Customer
                </h6>
                {!birthday.customerNumber ? (
                  <div className="text-muted fst-italic">No customer linked.</div>
                ) : isLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
                  </div>
                ) : isError || !customer ? (
                  <div className="alert alert-warning mb-0">The customer's details could not be loaded.</div>
                ) : (
                  <>
                    <div className="row g-3 mb-3">
                      <Detail
                        label={isCompany ? 'Company' : 'Name'}
                        value={isCompany ? info?.registeredName : `${info?.firstName || ''} ${info?.lastName || ''}`.trim()}
                      />
                      <Detail label="Customer number" value={customer.customerNumber} />
                      <Detail
                        label="Status"
                        value={customer.status && (
                          <span className={`badge ${customer.status === 'ACTIVE' ? 'bg-success' : 'bg-secondary'}`}>{customer.status}</span>
                        )}
                      />
                      <Detail label="Preferred channel" value={customer.preferredCommChannel} />
                      {isCompany && (
                        <>
                          <Detail label="Legal entity" value={getLegalEntityType(info?.legalEntityType)?.label ?? info?.legalEntityType} />
                          <Detail label="Registration number" value={info?.registrationNumber} />
                          <Detail label="Industry" value={info?.industrySector} />
                          <Detail label="Active directors" value={activeDirectors.length || undefined} />
                        </>
                      )}
                      {!isCompany && <Detail label="Occupation" value={info?.occupation} />}
                    </div>

                    <div className="pt-3 border-top">
                      <div className="small text-muted mb-2">{isCompany ? 'Company contacts' : 'Contacts'}</div>
                      <ContactList contacts={customer.contacts as ContactLike[]} empty="No contacts on record." />
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {birthday?.customerId && (
          <Link to={`/loan-customers/${birthday.customerId}/view`} className="btn btn-primary">
            <i className="fa fa-external-link me-2"></i>Open full customer profile
          </Link>
        )}
        <button type="button" className="btn btn-outline-secondary" onClick={onHide}>Close</button>
      </Modal.Footer>
    </Modal>
  );
};

export default BirthdayCustomerModal;
